import React, { FC } from "react";
import dynamic from "next/dynamic";
import {
    Container,
    Box,
    PictureProps,
} from "@smile2impress/components/relaunch";
import {
    Product,
    TreatmentCase,
    UTSLocale,
} from "@smile2impress/attraction-constants";
import { Awards } from "templates/relaunch/Awards";
import { BannerPaidSocial } from "templates/relaunch/BannerPaidSocial";
import { ClinicCompanyType } from "@smile2impress/location-content";
import { MainBanner } from "templates/relaunch/MainBanner";
import { MainBannerHeader } from "templates/relaunch/MainBanner/components";
import { MainBannerExtra } from "layouts/relaunch/pages/MainPageLayout/components/MainBannerExtra";
import { TwoColumnList } from "templates/relaunch/TwoColumnList";
import { TextWithCTA } from "templates/relaunch/TextWithCTA";
import { TextWithCTAFiftyFifty } from "templates/relaunch/TextWithCTAFiftyFifty";
import { AdvantagesWithHeading } from "templates/relaunch/AdvantagesWithHeading";
import { MultiColumnSlider } from "templates/relaunch/MultiColumnSlider";
import { LargePicture } from "templates/relaunch/LargePicture";
import { Slider } from "templates/relaunch/Slider";
import { TableWithCheckmarks } from "templates/relaunch/TableWithCheckmarks";
import { VerticalListWithImage } from "templates/relaunch/VerticalListWithImage";
import { VerticalListSection } from "templates/relaunch/VerticalListSection";
import { TreatmentIncludesSection } from "templates/relaunch/TreatmentIncludesSection";
import {
    STRAPI_WEBSITE_API_URL,
    ComponentsType,
    ComponentProps,
    ComponentMainBannerProps,
    ComponentMainBannerExtraProps,
    ComponentTwoColumnListProps,
    ComponentTextWithCTAProps,
    ComponentTextWithCTAFiftyFiftyProps,
    ComponentAdvantagesWithHeadingProps,
    ComponentMultiColumnSliderProps,
    ComponentQuestionsWithImageProps,
    ComponentQuestionsProps,
    ComponentMapProps,
    ComponentLargePictureProps,
    CmsPagesProps,
    ElementCtaProps,
    ElementResponsiveImageProps,
    ComponentPriceBlockProps,
    ComponentLeadFormProps,
    ComponentTreatmentInfoProps,
    ComponentMultiColumnProps,
    ComponentVerticalAdvantagesProps,
    ComponentHalfTextAndImageBannerProps,
    ComponentVerticalListWithImageProps,
    ComponentVerticalListSectionProps,
    ComponentTreatmentIncludesSectionProps,
    ComponentSliderProps,
    ComponentTableWithCheckmarksProps,
    ComponentIllustratedListProps,
    ComponentMultiColumnColumnType,
    ComponentTabbedContentProps,
    ComponentTabbedContentTabItemProps,
    ComponentBannerPaidSocialProps,
    ComponentClinicsListProps,
    ComponentClinicsMapProps,
    ComponentMainBannerHeaderProps,
    ComponentImageCardGridProps,
    ComponentInfoBannerProps,
    ComponentListWithCTAProps,
    ComponentFollowUsProps,
    ComponentVideoProps,
    ComponentReferralBannerProps,
    ComponentReferralHeroProps,
    ComponentTermLinkProps,
} from "api/cms-website";
import {
    Enum_Componentcomponentsleadform_Defaultpatientcategory,
    Enum_Componentcomponentsleadform_Defaulttreatmenttype,
    Enum_Componentcomponentstabbedcontent_Type,
} from "api/cms-website/generated/graphql";
import { TreatmentInfo } from "layouts/relaunch/pages/PricesLayout/components/TreatmentInfo";
import {
    Column,
    DoctorsProfile,
    SimpleColumn,
} from "templates/relaunch/MultiColumn/components";
import { MultiColumn } from "templates/relaunch/MultiColumn";
import { MultiColumnContent } from "templates/relaunch/MultiColumn/MultiColumn.types";
import { VerticalAdvantages } from "templates/relaunch/VerticalAdvantages";
import { HalfTextAndImageBanner } from "templates/relaunch/HalfTextAndImageBanner";
import { IllustratedList } from "templates/relaunch/IllustratedList";
import {
    TabbedContent,
    TABBED_CONTENT_TYPE,
    type TabbedContentProps,
    type TabType,
} from "templates/relaunch/TabbedContent";
import { InfoBanner } from "templates/relaunch/InfoBanner";
import { ImageCardGrid } from "templates/relaunch/ImageCardGrid";
import { ListWithCTA } from "templates/relaunch/ListWithCTA";
import { FollowUs } from "templates/relaunch/FollowUs";
import { ReferralBanner } from "templates/relaunch/ReferralBanner";
import { ReferralHero } from "templates/relaunch/ReferralHero";

import { Video } from "components/Video";
import { CitiesList } from "components/Relaunch/Locations/CitiesList";
import { ClinicsList } from "components/Relaunch/Locations/ClinicsList";
import { ClinicsMap as ClinicsMapComponent } from "components/Relaunch/Locations/ClinicsMap";
import { QuestionsWithImage } from "components/Relaunch/QuestionsWithImage";
import { Questions } from "components/Relaunch/Questions";
import { CtaButtonProps } from "components/CtaButton/CtaButton.types";
import { RichText } from "components/RichText";
import { PriceBlock } from "components/Relaunch/PriceBlock";

import filterClinicsFromBooking from "./filterClinicsFromBooking";
import { useUTSLocaleContext } from "../context/utsLocaleContext";
import { getImageUrl, getImageExtenstion } from "./imageUrl";
import getIcon from "./getIcon";
import {
    mapImage,
    mapItemsWithIcon,
    mapLink,
    mapTreatmentInfoItems,
    mapTwoColumnListItems,
} from "./cmsElementsMappers";
import { mapMultiColumnItems } from "./cmsElementsMappers/mapMultiColumnItems";
import { replaceTreatmentCasePlaceholders } from "./replaceTreatmentCasePlaceholders";
import { cmsColorEnumToThemePath } from "./cmsColorEnumToThemePath";
import { cmsCtaTestIdToDataTestId } from "./cmsCtaTestIdToDataTestId";

const ClinicsMap = dynamic(
    () =>
        import("components/ClinicsMap").then(({ ClinicsMap }) => ({
            default: ClinicsMap,
        })),
    { ssr: false }
);

const LeadFormWithImage = dynamic(
    () =>
        import("templates/relaunch/LeadFormWithImage").then(
            ({ LeadFormWithImage }) => ({
                default: LeadFormWithImage,
            })
        ),
    { ssr: false }
);

function mapCta(cta: ElementCtaProps, locale: UTSLocale): CtaButtonProps {
    const { iconRight, link = { href: "" }, ...rest } = cta;

    return {
        ...rest,
        link: mapLink(link.href, locale),
        ...(iconRight?.type && {
            iconRight: getIcon(iconRight.type),
        }),
    };
}

function mapColumnType(
    columnType: ComponentMultiColumnColumnType
): FC<any> | undefined {
    switch (columnType) {
        case ComponentMultiColumnColumnType.PRODUCTS:
            return SimpleColumn;
        case ComponentMultiColumnColumnType.DOCTORS:
            return DoctorsProfile;
        case ComponentMultiColumnColumnType.LONG_TEXT:
            return Column;
        case ComponentMultiColumnColumnType.ICONS:
            return Column;
        default:
            return undefined;
    }
}

/** Default mb when noMarginBottom is false. Use for Container wrapping CMS components. */
function getContainerMb(
    noMarginBottom: boolean | undefined,
    defaultMb: [number, number, number]
): [number, number, number] | undefined {
    return noMarginBottom ? undefined : defaultMb;
}

function mapSliderImages(
    images: Array<ElementResponsiveImageProps>
): [PictureProps, PictureProps] | undefined {
    if (
        images.length < 2 ||
        !images[0]?.desktop?.url ||
        !images[1]?.desktop?.url
    )
        return undefined;

    const firstImageUrl = getImageUrl(
        images[0].desktop.url,
        STRAPI_WEBSITE_API_URL
    );
    const secondImageUrl = getImageUrl(
        images[1].desktop.url,
        STRAPI_WEBSITE_API_URL
    );

    return [
        { defaultSrc: firstImageUrl, loading: "observer" },
        { defaultSrc: secondImageUrl, loading: "observer" },
    ];
}

const mapTabbedContentItemForTemplate = (
    item: ComponentTabbedContentTabItemProps
): TabType["content"][number] => {
    if (
        item.image &&
        typeof item.image === "object" &&
        "desktop" in item.image &&
        item.image.desktop?.url
    ) {
        return {
            ...item,
            image: mapImage(item.image as ElementResponsiveImageProps, true),
        };
    }
    return item;
};

const mapTabbedContentTabsForTemplate = (
    tabs: ComponentTabbedContentProps["content"]["tabs"]
): TabType[] =>
    tabs.map((tab) => ({
        title: tab.title,
        ...(tab.subtitle != null &&
            tab.subtitle !== "" && { subtitle: tab.subtitle }),
        content: tab.content.map(mapTabbedContentItemForTemplate),
    }));

const buildTabbedContentProps = (
    cms: ComponentTabbedContentProps
): TabbedContentProps => ({
    content: {
        header: cms.content.header,
        tabs: mapTabbedContentTabsForTemplate(cms.content.tabs),
        showMoreButtonLabel: cms.content.showMoreButtonLabel,
        ...(cms.content.rating != null && { rating: cms.content.rating }),
        ...(cms.content.disclaimer != null &&
            cms.content.disclaimer !== "" && {
                disclaimer: cms.content.disclaimer,
            }),
    },
    type:
        cms.type === Enum_Componentcomponentstabbedcontent_Type.TwoThreeColumn
            ? TABBED_CONTENT_TYPE.TWO_THREE_COLUMN
            : TABBED_CONTENT_TYPE.FOUR_COLUMN,
});

export function CmsComponentMatcher(
    component: ComponentProps,
    {
        locations,
        isPreview,
        products,
    }: Pick<CmsPagesProps, "locations" | "isPreview" | "products">
) {
    const { locale } = useUTSLocaleContext();
    const { cities, clinics, flagshipClinics } = locations || {};
    const priceReplacementOptions =
        products?.length && locale ? { products, locale } : null;

    switch (component?.__component) {
        case ComponentsType.AWARDS:
            return (
                <Container
                    mb={getContainerMb(component.noMarginBottom, [3, 3, 4])}
                >
                    <Awards />
                </Container>
            );
        case ComponentsType.BANNER_PAID_SOCIAL: {
            const bannerPaidSocial =
                component as ComponentBannerPaidSocialProps;
            return (
                <Container
                    variant="noMobilePX"
                    mb={getContainerMb(
                        bannerPaidSocial.noMarginBottom,
                        [3, 3, 5]
                    )}
                >
                    <BannerPaidSocial
                        content={{
                            title: bannerPaidSocial.title,
                            text: bannerPaidSocial.text,
                            badge: bannerPaidSocial.badge,
                            cta: mapCta(bannerPaidSocial.cta, locale),
                            image: mapImage(bannerPaidSocial.image, true),
                            advantages: bannerPaidSocial.advantages ?? [],
                            showInvisalignLogo:
                                bannerPaidSocial.showInvisalignLogo,
                        }}
                        headingAs="h1"
                    />
                </Container>
            );
        }
        case ComponentsType.MAIN_BANNER:
            const mainBanner = component as ComponentMainBannerProps;
            return (
                <Container
                    variant="noMobilePX"
                    mb={getContainerMb(mainBanner.noMarginBottom, [3, 3, 5])}
                >
                    <MainBanner
                        content={{
                            ...mainBanner,
                            slogans: mainBanner.slogans?.map(
                                ({ text }) => text
                            ),
                            cta: mapCta(mainBanner.cta, locale),
                            image: mapImage(mainBanner.image),
                        }}
                        headingAs="h1"
                    />
                </Container>
            );
        case ComponentsType.MAIN_BANNER_HEADER: {
            const bannerHeader = component as ComponentMainBannerHeaderProps;
            return (
                <Container
                    mb={getContainerMb(bannerHeader.noMarginBottom, [3, 3, 4])}
                >
                    <MainBannerHeader
                        title={bannerHeader.title}
                        text={bannerHeader.text}
                        subtitle={bannerHeader.subtitle}
                        cta={
                            bannerHeader.cta
                                ? mapCta(bannerHeader.cta, locale)
                                : undefined
                        }
                        headingAs="h1"
                        headingTextStyle="H1"
                    />
                </Container>
            );
        }
        case ComponentsType.MAIN_BANNER_EXTRA: {
            const mainBannerExtra = component as ComponentMainBannerExtraProps;
            const mainBannerExtraVariant = mainBannerExtra.invisalign
                ? { invisalign: true as const }
                : {
                      trustpilotLink:
                          mainBannerExtra.trustpilotLink?.href ?? "",
                  };
            return (
                <Container
                    variant="noMobilePX"
                    mb={getContainerMb(
                        mainBannerExtra.noMarginBottom,
                        [3, 3, 5]
                    )}
                >
                    <MainBannerExtra
                        {...mainBannerExtraVariant}
                        title={mainBannerExtra.title}
                        advantages={mainBannerExtra.advantages?.map(
                            ({ text }) =>
                                replaceTreatmentCasePlaceholders(
                                    text,
                                    priceReplacementOptions
                                )
                        )}
                        disclaimer={mainBannerExtra.disclaimer}
                    />
                </Container>
            );
        }
        case ComponentsType.TWO_COLUMN_LIST:
            const twoColumnList = component as ComponentTwoColumnListProps;
            const twoColumnListItems = mapTwoColumnListItems(
                twoColumnList.items,
                locale
            );
            return (
                <Container
                    mb={getContainerMb(twoColumnList.noMarginBottom, [3, 3, 4])}
                >
                    <TwoColumnList
                        content={{
                            ...twoColumnList,
                            items: twoColumnListItems,
                        }}
                    />
                </Container>
            );
        case ComponentsType.TEXT_WITH_CTA:
            const textWithCTA = component as ComponentTextWithCTAProps;

            return (
                <Container
                    mb={getContainerMb(textWithCTA.noMarginBottom, [3, 3, 4])}
                >
                    <TextWithCTA
                        content={{
                            title: textWithCTA.title,
                            text: textWithCTA.text,
                            cta: {
                                text: textWithCTA.cta?.text ?? "",
                                link: textWithCTA.cta?.link?.href,
                            },
                            ...(textWithCTA.image && {
                                image: mapImage(textWithCTA.image, true),
                            }),
                        }}
                    />
                </Container>
            );
        case ComponentsType.TEXT_WITH_CTA_FIFTY_FIFTY:
            const textWithCTAFiftyFifty =
                component as ComponentTextWithCTAFiftyFiftyProps;

            return (
                <Container
                    mb={getContainerMb(
                        textWithCTAFiftyFifty.noMarginBottom,
                        [3, 3, 5]
                    )}
                >
                    <TextWithCTAFiftyFifty
                        content={{
                            ...textWithCTAFiftyFifty,
                            cta: mapCta(textWithCTAFiftyFifty.cta, locale),
                            icon: textWithCTAFiftyFifty.icon
                                ? textWithCTAFiftyFifty.icon.type
                                : undefined,
                            textColor: cmsColorEnumToThemePath(
                                textWithCTAFiftyFifty.textColor?.type
                            ),
                            bgColor: cmsColorEnumToThemePath(
                                textWithCTAFiftyFifty.bgColor?.type
                            ),
                        }}
                    />
                </Container>
            );
        case ComponentsType.ADVANTAGES_WITH_HEADING:
            const advantagesWithHeading =
                component as ComponentAdvantagesWithHeadingProps;
            return (
                <Container
                    mb={getContainerMb(
                        advantagesWithHeading.noMarginBottom,
                        [4, 4, 5]
                    )}
                    variant="noMobilePX"
                >
                    <AdvantagesWithHeading
                        content={{
                            ...advantagesWithHeading,
                            cta: mapCta(advantagesWithHeading.cta, locale),
                        }}
                    />
                </Container>
            );
        case ComponentsType.MULTI_COLUMN_SLIDER:
            const multiColumnSlider =
                component as ComponentMultiColumnSliderProps;
            const multiColumnSliderItems = multiColumnSlider.items
                .filter((item) => item.image.length >= 2)
                .map((item) => {
                    const imageTuple = mapSliderImages(item.image);
                    const captionsTuple: [string, string] | undefined =
                        item.captions?.length === 2
                            ? [item.captions[0].text, item.captions[1].text]
                            : undefined;
                    return {
                        image: imageTuple
                            ? { image: imageTuple, captions: captionsTuple }
                            : undefined,
                    };
                });

            return (
                <Container
                    mb={getContainerMb(
                        multiColumnSlider.noMarginBottom,
                        [4, 4, 5]
                    )}
                >
                    <MultiColumnSlider
                        content={{
                            ...multiColumnSlider,
                            cta: multiColumnSlider.cta
                                ? mapCta(multiColumnSlider.cta, locale)
                                : undefined,
                            items: multiColumnSliderItems,
                            direction: multiColumnSlider.direction,
                        }}
                    />
                </Container>
            );
        case ComponentsType.QUESTIONS_WITH_IMAGE:
            const questionsWithImage =
                component as ComponentQuestionsWithImageProps;
            const questionsWithImageQuestions = mapItemsWithIcon(
                questionsWithImage.questions.items
            );
            return (
                <Container
                    mb={getContainerMb(
                        questionsWithImage.noMarginBottom,
                        [4, 4, 5]
                    )}
                >
                    <QuestionsWithImage
                        content={{
                            title: questionsWithImage.title,
                            image: mapImage(questionsWithImage.image, true),
                            questions: {
                                ...questionsWithImage.questions,
                                items: questionsWithImageQuestions,
                            },
                            ...(questionsWithImage.cta &&
                                mapCta(questionsWithImage.cta, locale) && {
                                    cta: mapCta(questionsWithImage.cta, locale),
                                }),
                            ...(questionsWithImage.ctaTestId && {
                                ctaTestId: cmsCtaTestIdToDataTestId(
                                    questionsWithImage.ctaTestId
                                ),
                            }),
                        }}
                    />
                </Container>
            );
        case ComponentsType.MAP: {
            const mapComponent = component as ComponentMapProps;
            if (!flagshipClinics || !clinics) return null;

            return (
                <Container
                    variant="noPX"
                    mb={getContainerMb(mapComponent.noMarginBottom, [4, 4, 5])}
                >
                    <ClinicsMap
                        flagshipClinics={flagshipClinics}
                        clinics={clinics}
                        searchBarTitle={mapComponent.title}
                    />
                </Container>
            );
        }
        case ComponentsType.QUESTIONS:
            const questions = component as ComponentQuestionsProps;
            const questionsItems = mapItemsWithIcon(questions.items);
            return (
                <Container
                    mb={getContainerMb(questions.noMarginBottom, [4, 4, 5])}
                >
                    <Questions
                        content={{
                            ...questions,
                            items: questionsItems,
                        }}
                    />
                </Container>
            );
        case ComponentsType.LARGE_PICTURE:
            const largePicture = component as ComponentLargePictureProps;
            const mappedLargeImage = mapImage(largePicture.image, true);
            return (
                <Container
                    variant="noMobilePX"
                    mb={getContainerMb(largePicture.noMarginBottom, [4, 4, 5])}
                >
                    <LargePicture
                        content={{
                            ...(mappedLargeImage && {
                                image: mappedLargeImage,
                            }),
                        }}
                    />
                </Container>
            );
        case ComponentsType.PRICE_BLOCK:
            const priceBlock = component as ComponentPriceBlockProps;
            const {
                title,
                subtitle,
                hideCasesList,
                hideMonthlyPrice,
                hideDurationGraph,
                hideBiteCorrection,
                hideCaseFullName,
                alwaysShowTnC,
                casesList,
                detailsList,
            } = priceBlock;
            const customCasesList =
                casesList && casesList?.length > 0
                    ? (casesList.map(({ text }) => text) as TreatmentCase[])
                    : [];
            // Applies the same "what's included" list to every case in the
            // block (the widget looks it up per case slug).
            const customDetailsList =
                detailsList &&
                detailsList.length > 0 &&
                customCasesList.length > 0
                    ? (Object.fromEntries(
                          customCasesList.map((slug) => [
                              slug,
                              Object.fromEntries(
                                  detailsList.map(({ label, value }) => [
                                      label,
                                      value,
                                  ])
                              ),
                          ])
                      ) as Record<TreatmentCase, Record<string, string>>)
                    : undefined;
            return (
                <Container
                    mb={getContainerMb(priceBlock.noMarginBottom, [4, 4, 4])}
                    variant="noMobilePX"
                >
                    <PriceBlock
                        hideCasesList={hideCasesList ?? false}
                        hideMonthlyPrice={hideMonthlyPrice ?? false}
                        hideDurationGraph={hideDurationGraph ?? false}
                        hideBiteCorrection={hideBiteCorrection ?? false}
                        hideCaseFullName={hideCaseFullName ?? false}
                        alwaysShowTnC={alwaysShowTnC ?? false}
                        products={products}
                        {...(customCasesList.length > 0 && {
                            cases: customCasesList,
                        })}
                        contentOverride={{
                            title,
                            description: subtitle,
                            ...(customDetailsList && {
                                detailsList: customDetailsList,
                            }),
                        }}
                    />
                </Container>
            );
        case ComponentsType.LEAD_FORM:
            const leadForm = component as ComponentLeadFormProps;
            const {
                image: leadFormImage,
                title: leadFormTitle,
                showTreatmentTypeSelector,
                defaultTreatmentType,
                showPromoCodeCTA,
                showPatientCategorySelector,
                defaultPatientCategory,
            } = leadForm;
            const mappedImage = mapImage(leadFormImage, true);

            return (
                <Container
                    mb={getContainerMb(leadForm.noMarginBottom, [4, 4, 5])}
                >
                    <LeadFormWithImage
                        title={leadFormTitle}
                        image={mappedImage}
                        defaultPatientCategory={
                            defaultPatientCategory ??
                            Enum_Componentcomponentsleadform_Defaultpatientcategory.Adult
                        }
                        defaultTreatmentType={
                            defaultTreatmentType ??
                            Enum_Componentcomponentsleadform_Defaulttreatmenttype.Orthodontics
                        }
                        withPatientCategory={
                            showPatientCategorySelector ?? false
                        }
                        withPromoCode={showPromoCodeCTA ?? false}
                        withTreatmentType={showTreatmentTypeSelector ?? true}
                    />
                </Container>
            );
        case ComponentsType.TREATMENT_INFO:
            const treatmentInfo = component as ComponentTreatmentInfoProps;
            return (
                <Container
                    mb={getContainerMb(treatmentInfo.noMarginBottom, [4, 4, 5])}
                    variant="noMobilePX"
                >
                    <TreatmentInfo
                        content={{
                            title: treatmentInfo.title,
                            subtitle: treatmentInfo.subtitle,
                            items: mapTreatmentInfoItems(treatmentInfo.items),
                        }}
                    />
                </Container>
            );
        case ComponentsType.MULTI_COLUMN:
            const multiColumn = component as ComponentMultiColumnProps;
            const mappedItems = mapMultiColumnItems({
                items: multiColumn.items,
                products: products as Product[],
                locale,
                columnType:
                    multiColumn.columnType ||
                    ComponentMultiColumnColumnType.PRODUCTS,
            });
            const columnComponent = mapColumnType(
                multiColumn.columnType ||
                    ComponentMultiColumnColumnType.PRODUCTS
            );
            return (
                <Container
                    mb={getContainerMb(multiColumn.noMarginBottom, [4, 4, 5])}
                >
                    <MultiColumn
                        content={{
                            mobileSlider: multiColumn.mobileSlider ?? true,
                            narrowColumns:
                                multiColumn.columnType ===
                                ComponentMultiColumnColumnType.DOCTORS
                                    ? false
                                    : multiColumn.narrowColumns ?? true,
                            ignoreMinimumGrid:
                                multiColumn.columnType ===
                                    ComponentMultiColumnColumnType.ICONS ||
                                multiColumn.columnType ===
                                    ComponentMultiColumnColumnType.LONG_TEXT ||
                                multiColumn.columnType ===
                                    ComponentMultiColumnColumnType.DOCTORS
                                    ? true
                                    : mappedItems.length >= 4,
                            title: multiColumn.title,
                            subtitle: multiColumn.subtitle,
                            items: mappedItems as MultiColumnContent["items"],
                            cta: multiColumn.cta
                                ? mapCta(multiColumn.cta, locale)
                                : undefined,
                            ...(multiColumn.gridSpacing && {
                                gridSpacing: multiColumn.gridSpacing,
                            }),
                            ...(multiColumn.gridRows && {
                                gridRows: multiColumn.gridRows,
                            }),
                            ...(multiColumn.mobileGridRows && {
                                mobileGridRows: multiColumn.mobileGridRows,
                            }),
                            ...(multiColumn.gridRowGap && {
                                gridRowGap: multiColumn.gridRowGap,
                            }),
                            ...(multiColumn.gridRows && {
                                itemContent: { mobileColumn: true },
                            }),
                        }}
                        itemComponent={columnComponent}
                    />
                </Container>
            );
        case ComponentsType.VERTICAL_ADVANTAGES:
            const verticalAdvantages =
                component as ComponentVerticalAdvantagesProps;
            const additionalVerticalAdvantagesContent =
                verticalAdvantages.advantages &&
                verticalAdvantages.advantages.length > 0
                    ? { advantages: verticalAdvantages.advantages }
                    : verticalAdvantages.image
                    ? { image: mapImage(verticalAdvantages.image, true) }
                    : {};
            return (
                <Container
                    mb={getContainerMb(
                        verticalAdvantages.noMarginBottom,
                        [4, 4, 5]
                    )}
                    variant="noMobilePX"
                >
                    <VerticalAdvantages
                        content={{
                            title: verticalAdvantages.title,
                            subtitle: verticalAdvantages.subtitle,
                            cta: verticalAdvantages.cta
                                ? mapCta(verticalAdvantages.cta, locale)
                                : undefined,
                            ...additionalVerticalAdvantagesContent,
                        }}
                    />
                </Container>
            );
        case ComponentsType.HALF_TEXT_AND_IMAGE_BANNER: {
            const halfTextAndImageBanner =
                component as ComponentHalfTextAndImageBannerProps;
            return (
                <Container
                    mb={getContainerMb(
                        halfTextAndImageBanner.noMarginBottom,
                        [4, 4, 5]
                    )}
                    px={[0, 0, 4]}
                >
                    <HalfTextAndImageBanner
                        content={{
                            title: halfTextAndImageBanner.title,
                            subtitle: halfTextAndImageBanner.subtitle,
                            ...(halfTextAndImageBanner.image
                                ? {
                                      image: mapImage(
                                          halfTextAndImageBanner.image,
                                          true
                                      ),
                                  }
                                : {}),
                        }}
                    />
                </Container>
            );
        }
        case ComponentsType.VERTICAL_LIST_WITH_IMAGE:
            const verticalListWithImage =
                component as ComponentVerticalListWithImageProps;
            return (
                <Container
                    mb={getContainerMb(
                        verticalListWithImage.noMarginBottom,
                        [4, 4, 5]
                    )}
                >
                    <VerticalListWithImage
                        content={{
                            title: verticalListWithImage.title,
                            bgImage: mapImage(
                                verticalListWithImage.bgImage,
                                true
                            ),
                            items: (verticalListWithImage.items || []).map(
                                (item) => ({
                                    title: item.title,
                                    text: item.text,
                                    ...(item.icon && {
                                        icon: item.icon.type,
                                    }),
                                })
                            ),
                            subtitle: verticalListWithImage.subtitle,
                            numberedItems: verticalListWithImage.numberedItems,
                            ...(verticalListWithImage.cta && {
                                cta: mapCta(verticalListWithImage.cta, locale),
                            }),
                        }}
                    />
                </Container>
            );
        case ComponentsType.VERTICAL_LIST_SECTION: {
            const verticalListSection =
                component as ComponentVerticalListSectionProps;
            return (
                <Container
                    variant="noMobilePX"
                    mb={getContainerMb(
                        verticalListSection.noMarginBottom,
                        [4, 4, 5]
                    )}
                >
                    <VerticalListSection
                        headingAs="h2"
                        content={{
                            title: verticalListSection.title,
                            subtitle: verticalListSection.subtitle,
                            items: (verticalListSection.items || []).map(
                                (item) => ({
                                    title: item.title,
                                    texts: (item.texts || []).map(
                                        (t) => t.text
                                    ),
                                    ...(item.icon && {
                                        icon: item.icon.type,
                                    }),
                                })
                            ),
                            ...(verticalListSection.cta && {
                                cta: mapCta(verticalListSection.cta, locale),
                            }),
                        }}
                    />
                </Container>
            );
        }
        case ComponentsType.TREATMENT_INCLUDES_SECTION: {
            const treatmentIncludesSection =
                component as ComponentTreatmentIncludesSectionProps;
            return (
                <Container
                    variant="noMobilePX"
                    mb={getContainerMb(
                        treatmentIncludesSection.noMarginBottom,
                        [4, 4, 5]
                    )}
                >
                    <TreatmentIncludesSection
                        headingAs="h2"
                        content={{
                            title: treatmentIncludesSection.title,
                            image: mapImage(
                                treatmentIncludesSection.image,
                                true
                            ),
                            items: (treatmentIncludesSection.items || []).map(
                                (item) => item.text
                            ),
                        }}
                    />
                </Container>
            );
        }
        case ComponentsType.SLIDER:
            const slider = component as ComponentSliderProps;
            return (
                <Container
                    mb={getContainerMb(slider.noMarginBottom, [5, 5, 5])}
                >
                    <Slider
                        content={{
                            title: slider.title,
                            items: (slider.items || []).map((item) => ({
                                title: item.title,
                                text: item.text,
                                rating: item.rating,
                                customerName: item.customerName,
                                date: item.date,
                            })),
                            trustpilotLink: slider.trustpilotLink,
                            disclaimer: slider.disclaimer,
                            ...(slider.cta && {
                                cta: mapCta(slider.cta, locale),
                            }),
                        }}
                    />
                </Container>
            );
        case ComponentsType.TABLE_WITH_CHECKMARKS:
            const tableWithCheckmarks =
                component as ComponentTableWithCheckmarksProps;
            return (
                <Container
                    mb={getContainerMb(
                        tableWithCheckmarks.noMarginBottom,
                        [4, 4, 5]
                    )}
                    bgColor={["base.grey", "base.grey", "initial"]}
                >
                    <TableWithCheckmarks
                        content={{
                            title: tableWithCheckmarks.title,
                            columns: tableWithCheckmarks.columns || [],
                            rows: (tableWithCheckmarks.rows || []).map(
                                (row) => ({
                                    title: row.title,
                                    columns: row.columns,
                                })
                            ),
                            cta: {
                                text: tableWithCheckmarks.cta?.text ?? "",
                                link: tableWithCheckmarks.cta?.link?.href,
                            },
                        }}
                    />
                </Container>
            );
        case ComponentsType.TABBED_CONTENT:
            const tabbedContent = component as ComponentTabbedContentProps;
            const tabbedContentProps = buildTabbedContentProps(tabbedContent);

            return (
                <Container
                    mb={getContainerMb(tabbedContent.noMarginBottom, [4, 4, 5])}
                    variant="noMobilePX"
                >
                    <TabbedContent {...tabbedContentProps} />
                </Container>
            );
        case ComponentsType.ILLUSTRATED_LIST:
            const illustratedList = component as ComponentIllustratedListProps;
            const additionalIllustratedListContent = illustratedList.subtitle
                ? { subtitle: illustratedList.subtitle }
                : illustratedList.text
                ? { text: illustratedList.text }
                : {};
            return (
                <Container
                    mb={getContainerMb(
                        illustratedList.noMarginBottom,
                        [4, 4, 5]
                    )}
                >
                    <IllustratedList
                        content={{
                            title: illustratedList.title,
                            withDivider: illustratedList.withDivider,
                            withHeaderCta: illustratedList.withHeaderCta,
                            withMobileCta: illustratedList.withMobileCta,
                            withNumberHeading:
                                illustratedList.withNumberHeading,
                            withExpandableText:
                                illustratedList.withExpandableText,
                            ...additionalIllustratedListContent,
                            items: illustratedList.items.map((item) => ({
                                title: item.title,
                                text: item.text as string,
                                image: mapImage(item.image, true),
                            })),
                            cta: illustratedList.cta
                                ? mapCta(illustratedList.cta, locale)
                                : undefined,
                            mainCTA: illustratedList.mainCTA
                                ? mapCta(illustratedList.mainCTA, locale)
                                : undefined,
                        }}
                    />
                </Container>
            );
        case ComponentsType.CLINICS_LIST: {
            const clinicsList = component as ComponentClinicsListProps;
            const countryWithFullDisclosure = [
                UTSLocale.PORTUGAL_PT,
                UTSLocale.ITALY_IT,
                UTSLocale.FRANCE_FR,
            ].includes(locale);

            if (countryWithFullDisclosure && clinics && flagshipClinics) {
                let partnersClinics = filterClinicsFromBooking(
                    clinics,
                    locale,
                    "list"
                );
                let b2bClinics;

                if ([UTSLocale.PORTUGAL_PT].includes(locale)) {
                    const clinicsThatAreActuallyB2B = [28, 29];
                    b2bClinics = partnersClinics.filter(
                        (c: any) =>
                            c.clinicCompanyType ===
                                ClinicCompanyType.PARTNER_CLINIC ||
                            clinicsThatAreActuallyB2B.includes(c.id)
                    );
                    partnersClinics = partnersClinics.filter(
                        (c: any) =>
                            c.clinicCompanyType ===
                                ClinicCompanyType.RENTED_BOX &&
                            !clinicsThatAreActuallyB2B.includes(c.id)
                    );
                }

                return (
                    <Container
                        mb={getContainerMb(
                            clinicsList.noMarginBottom,
                            [4, 4, 5]
                        )}
                    >
                        <ClinicsList
                            titleForFirstList={clinicsList.titleForFirstList}
                            titleForSecondList={clinicsList.titleForSecondList}
                            titleForThirdList={clinicsList.titleForThirdList}
                            disclaimerForSecondList={
                                clinicsList.disclaimerForSecondList
                            }
                            flagshipClinics={flagshipClinics}
                            defaultClinics={partnersClinics}
                            b2bClinics={b2bClinics}
                        />
                    </Container>
                );
            }

            if (cities) {
                return (
                    <Container
                        mb={getContainerMb(
                            clinicsList.noMarginBottom,
                            [4, 4, 5]
                        )}
                    >
                        <CitiesList
                            titleForFirstList={clinicsList.titleForFirstList}
                            titleForSecondList={clinicsList.titleForSecondList}
                            disclaimerForSecondList={
                                clinicsList.disclaimerForSecondList
                            }
                            cities={cities}
                        />
                    </Container>
                );
            }

            return null;
        }
        case ComponentsType.CLINICS_MAP: {
            const clinicsMapComponent = component as ComponentClinicsMapProps;
            const clinicsMapImageUrl = getImageUrl(
                clinicsMapComponent.image?.url || "",
                STRAPI_WEBSITE_API_URL
            );
            const clinicsMapImageExt = getImageExtenstion(clinicsMapImageUrl);
            const clinicsMapImageObj: PictureProps = {
                defaultSrc: clinicsMapImageUrl,
                images: [
                    clinicsMapImageExt
                        ? { [clinicsMapImageExt]: clinicsMapImageUrl }
                        : null,
                ].filter((i) => i),
                loading: "observer",
            };
            return (
                <Container
                    variant="noPX"
                    mb={getContainerMb(
                        clinicsMapComponent.noMarginBottom,
                        [4, 4, 5]
                    )}
                >
                    <ClinicsMapComponent
                        title={clinicsMapComponent.title}
                        image={clinicsMapImageObj}
                    />
                </Container>
            );
        }
        case ComponentsType.IMAGE_CARD_GRID: {
            const imageCardGrid = component as ComponentImageCardGridProps;
            return (
                <Container
                    mb={getContainerMb(imageCardGrid.noMarginBottom, [4, 4, 5])}
                >
                    <ImageCardGrid
                        content={{
                            title: imageCardGrid.title,
                            items: imageCardGrid.items.map((item) => ({
                                title: item.title,
                                text: item.text,
                                bg: mapImage(item.bg, true),
                            })),
                        }}
                    />
                </Container>
            );
        }
        case ComponentsType.INFO_BANNER: {
            const infoBanner = component as ComponentInfoBannerProps;
            return (
                <Container
                    mb={getContainerMb(infoBanner.noMarginBottom, [3, 3, 4])}
                >
                    <InfoBanner
                        content={{
                            title: infoBanner.title,
                            text: infoBanner.text,
                            bg: cmsColorEnumToThemePath(infoBanner.bg?.type),
                            iconColor: cmsColorEnumToThemePath(
                                infoBanner.iconColor?.type
                            ),
                            extraTitleStyles: {
                                color:
                                    cmsColorEnumToThemePath(
                                        infoBanner.titleColor?.type
                                    ) || "text.baseInverse",
                                textTransform: "none",
                            },
                            icon: infoBanner.icon?.type,
                            items: infoBanner.items?.length
                                ? infoBanner.items.map((item) => ({
                                      text: item.text,
                                      icon: item.icon?.type,
                                  }))
                                : undefined,
                            cta: infoBanner.cta
                                ? mapCta(infoBanner.cta, locale)
                                : undefined,
                        }}
                    />
                </Container>
            );
        }
        case ComponentsType.LIST_WITH_CTA: {
            const listWithCta = component as ComponentListWithCTAProps;
            return (
                <Container
                    mb={getContainerMb(listWithCta.noMarginBottom, [3, 3, 4])}
                >
                    <ListWithCTA
                        content={{
                            title: listWithCta.title,
                            text: listWithCta.text || "",
                            items: listWithCta.items?.length
                                ? listWithCta.items.map((item) => ({
                                      text: item.text,
                                      icon: item.icon?.type || "",
                                  }))
                                : [],
                            cta: listWithCta.cta
                                ? mapCta(listWithCta.cta, locale)
                                : { text: "" },
                        }}
                    />
                </Container>
            );
        }
        case ComponentsType.FOLLOW_US: {
            const followUs = component as ComponentFollowUsProps;
            return (
                <Container
                    mb={getContainerMb(followUs.noMarginBottom, [3, 3, 4])}
                >
                    <FollowUs
                        content={{
                            title: followUs.title,
                            subtitle: followUs.subtitle,
                            socials: followUs.socials
                                ?.filter((social) => social.icon?.type)
                                .map((social) => ({
                                    icon: getIcon(social.icon!.type),
                                    link: social.link,
                                })),
                        }}
                    />
                </Container>
            );
        }
        case ComponentsType.VIDEO: {
            const video = component as ComponentVideoProps;
            const videoPosterImage = video.poster
                ? mapImage(video.poster, true)
                : undefined;
            return (
                <Container mb={getContainerMb(video.noMarginBottom, [3, 3, 4])}>
                    {video.title && (
                        <Box mb={2} textStyle="title3">
                            {video.title}
                        </Box>
                    )}
                    {video.text && (
                        <Box mb={2} textStyle="body">
                            {video.text}
                        </Box>
                    )}
                    <Video
                        src={video.videoUrl}
                        srcMobile={video.videoUrlMobile}
                        poster={videoPosterImage?.defaultSrc}
                        controls
                        width="100%"
                    />
                    {video.caption && (
                        <Box mt={2} textStyle="body">
                            {video.caption}
                        </Box>
                    )}
                </Container>
            );
        }
        case ComponentsType.REFERRAL_BANNER: {
            const referralBanner = component as ComponentReferralBannerProps;
            const mappedCards = referralBanner.cards?.map((card) => ({
                title: card.title,
                text: card.text,
                bgColor: card.bgColor,
                textColor: card.textColor,
                imageUrl: card.image
                    ? getImageUrl(card.image.url, STRAPI_WEBSITE_API_URL)
                    : undefined,
            }));
            return (
                <Container
                    px={[2, 2, 0]}
                    mb={getContainerMb(
                        referralBanner.noMarginBottom,
                        [4, 4, 5]
                    )}
                >
                    <ReferralBanner
                        content={{
                            title: referralBanner.title,
                            subtitle: referralBanner.subtitle,
                            description: referralBanner.description,
                            buttonText: referralBanner.buttonText,
                            toastText: referralBanner.toastText,
                            cards: mappedCards,
                        }}
                    />
                </Container>
            );
        }
        case ComponentsType.REFERRAL_HERO: {
            const referralHero = component as ComponentReferralHeroProps;
            const mappedCard = referralHero.card
                ? {
                      title: referralHero.card.title,
                      text: referralHero.card.text,
                      bgColor: referralHero.card.bgColor,
                      textColor: referralHero.card.textColor,
                      imageUrl: referralHero.card.image
                          ? getImageUrl(
                                referralHero.card.image.url,
                                STRAPI_WEBSITE_API_URL
                            )
                          : undefined,
                  }
                : undefined;
            return (
                <Container
                    mt={3}
                    mb={getContainerMb(referralHero.noMarginBottom, [3, 3, 4])}
                >
                    <ReferralHero
                        content={{
                            title: referralHero.title,
                            subtitle: referralHero.subtitle,
                            description: referralHero.description,
                            card: mappedCard,
                        }}
                    />
                </Container>
            );
        }
        case ComponentsType.TERM_LINK: {
            const termLink = component as ComponentTermLinkProps;
            return (
                <Container
                    mb={getContainerMb(termLink.noMarginBottom, [4, 4, 5])}
                >
                    <Box px={[0, 0, 4]}>
                        <RichText content={termLink.text} />
                    </Box>
                </Container>
            );
        }
        default:
            if (!isPreview) {
                return null;
            }

            return (
                <Box>
                    Unsupported component:{" "}
                    {(component as ComponentProps)?.__component}
                </Box>
            );
    }
}
